import { useState } from 'react'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.gpmeta.au'

export default function Waitlist() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle')
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setStatus('submitting')
    setError(null)
    try {
      const res = await fetch(`${API_URL}/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, source: 'gate' }),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string }
        throw new Error(body.message || 'Sign-up failed')
      }
      setStatus('done')
    } catch (err) {
      setStatus('error')
      setError(err instanceof Error ? err.message : 'Unknown error')
    }
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center px-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">GPMeta</h1>
          <p className="text-sm font-semibold text-blue-700 uppercase tracking-wide mb-3">
            AHPRA-verified GPs only
          </p>
          <p className="text-lg text-gray-700">
            Every Australian clinical resource. One search.
          </p>
        </div>

        <div className="border border-gray-200 rounded-xl p-6 md:p-8 shadow-sm">
          <h2 className="text-xl font-bold mb-2">Closed beta — join the waitlist</h2>
          <p className="text-sm text-gray-600 mb-6">
            We are onboarding Australian GPs in small batches. Leave your details and we will email you
            when access opens.
          </p>

          {status === 'done' ? (
            <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg px-4 py-3 text-sm">
              You are on the list. Confirmation email on its way.
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label htmlFor="wl-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  id="wl-name"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Dr First Last"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label htmlFor="wl-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  id="wl-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@practice.com.au"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
                />
              </div>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-3 py-2 text-sm">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-60"
              >
                {status === 'submitting' ? 'Adding…' : 'Join the waitlist'}
              </button>
              <p className="text-xs text-gray-500 text-center">
                For healthcare professionals only. No spam.
              </p>
            </form>
          )}
        </div>

        <div className="text-center mt-6 text-xs text-gray-500 space-x-3">
          <a href="/terms" className="hover:text-gray-700">Terms</a>
          <span>·</span>
          <a href="/privacy" className="hover:text-gray-700">Privacy</a>
          <span>·</span>
          <a href="mailto:hello@gpmeta.au" className="hover:text-gray-700">hello@gpmeta.au</a>
        </div>
      </div>
    </div>
  )
}
